/**
 * API CLIENT CON RETRY AUTOMÁTICO Y RESILIENCIA
 * 
 * Características:
 * - Retry automático con backoff exponencial
 * - Detección de conexión offline
 * - Cache de respuestas para datos críticos
 * - Timeout configurable
 * - Manejo de errores centralizado
 */

import axios from 'axios';

const _BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// En producción (viadirectave.com) usar URLs relativas para evitar CORS y dependencia
// del REACT_APP_BACKEND_URL bakeado en el bundle. En dev/preview usar la variable de entorno.
const isProduction = typeof window !== 'undefined' && 
  window.location.hostname === 'viadirectave.com';
const API_BASE_URL = isProduction ? '' : _BACKEND_URL;

// Exportar solo API_BASE_URL
export { API_BASE_URL };

// Configuración de retry
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 segundo
  maxDelay: 10000, // 10 segundos
  retryableStatuses: [408, 429, 500, 502, 503, 504],
  retryableMethods: ['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE']
};

// Cache en memoria para datos críticos
const responseCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

/**
 * Delay con backoff exponencial
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const calculateBackoff = (attempt) => {
  const backoff = Math.min(
    RETRY_CONFIG.baseDelay * Math.pow(2, attempt),
    RETRY_CONFIG.maxDelay
  );
  // Añadir jitter para evitar thundering herd
  return backoff + Math.random() * 1000;
};

/**
 * Verificar si el error es retriable
 */
const isRetriable = (error, method) => {
  // No retry si estamos offline
  if (!navigator.onLine) return false;
  
  // No retry para métodos que modifican datos (excepto los configurados)
  if (!RETRY_CONFIG.retryableMethods.includes(method.toUpperCase())) {
    // Permitir retry de POST solo si es idempotente o timeout
    if (method.toUpperCase() === 'POST') {
      return error.code === 'ECONNABORTED' || error.message === 'Network Error';
    }
    return false;
  }
  
  // Retry en errores de red
  if (error.message === 'Network Error') return true;
  if (error.code === 'ECONNABORTED') return true;
  
  // Retry en ciertos códigos HTTP
  if (error.response?.status && RETRY_CONFIG.retryableStatuses.includes(error.response.status)) {
    return true;
  }
  
  return false;
};

/**
 * Cliente API con retry automático
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // 15 segundos
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para añadir token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para retry automático y manejo de token expirado
apiClient.interceptors.response.use(
  (response) => {
    // Cachear respuestas GET exitosas para datos críticos
    const cacheableRoutes = ['/api/cities', '/api/payment-methods', '/api/faqs'];
    if (response.config.method === 'get' && cacheableRoutes.some(r => response.config.url?.includes(r))) {
      const cacheKey = response.config.url;
      responseCache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });
    }
    return response;
  },
  async (error) => {
    const config = error.config;
    
    // ── TOKEN EXPIRADO (401): limpiar sesión y redirigir al login ──
    if (error.response?.status === 401) {
      const isAuthEndpoint = config.url?.includes('/auth/login') || 
                             config.url?.includes('/auth/register') ||
                             config.url?.includes('/auth/session');
      if (!isAuthEndpoint) {
        const hasToken = localStorage.getItem('token');
        if (hasToken) {
          console.warn('[apiClient] Token expirado — limpiando sesión');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('sessionId');
        }
      }
    }
    
    // Inicializar contador de retry
    config._retryCount = config._retryCount || 0;
    
    // Verificar si debemos hacer retry
    if (config._retryCount < RETRY_CONFIG.maxRetries && isRetriable(error, config.method)) {
      config._retryCount += 1;
      
      // Log en desarrollo
      if (process.env.NODE_ENV === 'development') {
      }
      
      // Esperar con backoff
      await delay(calculateBackoff(config._retryCount));
      
      // Reintentar
      return apiClient(config);
    }
    
    // Si falló y tenemos cache, usarlo como fallback para GET
    if (config.method === 'get') {
      const cached = responseCache.get(config.url);
      if (cached && (Date.now() - cached.timestamp) < CACHE_TTL * 2) {
        console.warn(`⚠️ Usando cache para ${config.url} debido a error de red`);
        return { data: cached.data, _fromCache: true };
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * Métodos de conveniencia con manejo de errores mejorado
 */
export const api = {
  /**
   * GET con soporte de cache
   */
  get: async (url, options = {}) => {
    try {
      const response = await apiClient.get(url, options);
      return response.data;
    } catch (error) {
      // Intentar cache si está disponible
      const cached = responseCache.get(`${API_BASE_URL}${url}`);
      if (cached && options.allowStaleCache) {
        return cached.data;
      }
      throw error;
    }
  },

  /**
   * POST con validación de conexión
   */
  post: async (url, data, options = {}) => {
    if (!navigator.onLine) {
      throw new Error('Sin conexión a internet. Verifica tu conexión e intenta de nuevo.');
    }
    const response = await apiClient.post(url, data, options);
    return response.data;
  },

  /**
   * PUT con validación de conexión
   */
  put: async (url, data, options = {}) => {
    if (!navigator.onLine) {
      throw new Error('Sin conexión a internet. Verifica tu conexión e intenta de nuevo.');
    }
    const response = await apiClient.put(url, data, options);
    return response.data;
  },

  /**
   * DELETE con validación de conexión
   */
  delete: async (url, options = {}) => {
    if (!navigator.onLine) {
      throw new Error('Sin conexión a internet. Verifica tu conexión e intenta de nuevo.');
    }
    const response = await apiClient.delete(url, options);
    return response.data;
  }
};

/**
 * Hook para verificar estado de conexión
 */
export const checkConnection = () => {
  return {
    online: navigator.onLine,
    effectiveType: navigator.connection?.effectiveType || 'unknown'
  };
};

/**
 * Limpiar cache (útil en logout)
 */
export const clearApiCache = () => {
  responseCache.clear();
};

export default apiClient;
